import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  mediaChip: {
    fontSize: "0.75rem",
    height: 24,
  },
  // Mobile
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  cardIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: "rgba(91,45,142,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#5B2D8E",
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardShortcode: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  cardMeta: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardActions: {
    display: "flex",
    flexShrink: 0,
    gap: 2,
  },
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  geralBadge: {
    fontSize: "0.7rem",
    height: 18,
    backgroundColor: "rgba(34,197,94,0.12)",
    color: "#166534",
    borderRadius: 4,
    padding: "0 5px",
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600,
  },
}));

const mediaIcons = {
  audio: "🎵",
  image: "🖼️",
  video: "🎥",
  document: "📎",
};

const mediaColors = {
  audio: "secondary",
  image: "primary",
  video: "default",
  document: "default",
};

const Quickemessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickmessage`, onQuickMessageEvent);
    return () => {
      socket.off(`company-${companyId}-quickmessage`, onQuickMessageEvent);
    };
  }, [socket]);

  const fetchQuickemessages = async () => {
    try {
      const companyId = user.companyId;
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getMediaLabel = (quickmessage) => {
    if (!quickmessage.mediaName) return null;
    const type = quickmessage.mediaType || "document";
    return { icon: mediaIcons[type] || "📎", type, color: mediaColors[type] || "default" };
  };

  const renderMobileCard = (quickemessage) => {
    const media = getMediaLabel(quickemessage);
    return (
      <div key={quickemessage.id} className={classes.card}>
        <div className={classes.cardIcon}>
          <FlashOnIcon fontSize="small" />
        </div>
        <div className={classes.cardBody}>
          <div className={classes.cardShortcode}>
            /{quickemessage.shortcode}
            {quickemessage.geral && (
              <span className={classes.geralBadge}>Global</span>
            )}
          </div>
          {media ? (
            <div className={classes.cardMeta}>
              {media.icon} {media.type}
            </div>
          ) : (
            <div className={classes.cardMeta}>
              {i18n.t("quickMessages.noAttachment")}
            </div>
          )}
        </div>
        <div className={classes.cardActions}>
          <IconButton size="small" onClick={() => handleEditQuickemessage(quickemessage)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setConfirmModalOpen(true);
              setDeletingQuickemessage(quickemessage);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />

      {isMobile ? (
        <>
          <MainHeader>
            <div className={classes.mobileHeader}>
              <div className={classes.mobileHeaderRow}>
                <Title>{i18n.t("quickMessages.title")}</Title>
                <Typography variant="caption" color="textSecondary">
                  {quickemessages.length} mensajes
                </Typography>
              </div>
              <TextField
                fullWidth
                size="small"
                placeholder={i18n.t("quickMessages.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            {quickemessages.length === 0 && !loading ? (
              <div className={classes.emptyState}>
                <FlashOnIcon style={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">Sin mensajes rápidos</Typography>
              </div>
            ) : (
              <div className={classes.mobileList}>
                {quickemessages.map(renderMobileCard)}
                {loading && (
                  <Typography variant="caption" align="center" color="textSecondary">
                    Cargando...
                  </Typography>
                )}
              </div>
            )}
          </Paper>

          <Fab
            color="primary"
            className={classes.fab}
            onClick={handleOpenQuickMessageDialog}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <>
          <MainHeader>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
              <Title>{i18n.t("quickMessages.title")}</Title>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <TextField
                  placeholder={i18n.t("quickMessages.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  size="small"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleOpenQuickMessageDialog}
                  color="primary"
                >
                  {i18n.t("quickMessages.buttons.add")}
                </Button>
              </div>
            </div>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    {i18n.t("quickMessages.table.shortcode")}
                  </TableCell>
                  <TableCell align="center">Mídia</TableCell>
                  <TableCell align="center">
                    {i18n.t("quickMessages.table.status")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("quickMessages.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {quickemessages.map((quickemessage) => {
                    const media = getMediaLabel(quickemessage);
                    return (
                      <TableRow key={quickemessage.id}>
                        <TableCell align="center">
                          /{quickemessage.shortcode}
                        </TableCell>
                        <TableCell align="center">
                          {media ? (
                            <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 6 }}>
                              <span>{media.icon}</span>
                              <Chip
                                size="small"
                                label={media.type}
                                color={media.color}
                                className={classes.mediaChip}
                              />
                            </Box>
                          ) : (
                            i18n.t("quickMessages.noAttachment")
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {quickemessage.geral === true ? (
                            <CheckCircleIcon style={{ color: "green" }} />
                          ) : (
                            ""
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditQuickemessage(quickemessage)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingQuickemessage(quickemessage);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {loading && <TableRowSkeleton columns={4} />}
                </>
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Quickemessages;
